/**
 * The example clip: one `<video>` element, shown inline in the hero frame
 * and, while the enlarged view is open, inside it — the same element, so
 * playback, position and sound carry across and both sets of controls stay
 * in sync, exactly as the reference moves its one video between the two.
 *
 * React owns the element through a portal into a slot the framework never
 * looks inside; the slot is a plain container this controller moves between
 * the inline host and the enlarged host with the DOM's own `append`. No
 * React child list ever changes underneath a moved node.
 *
 * Playback state is what the element reports (`play`, `pause`, `ended`,
 * `volumechange`), never a timer. The clip is muted, looping, inline and
 * loads only its metadata until it plays; it autoplays only when less
 * motion was not asked for, and then only once it has data.
 */

import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { useReducedMotion } from './motion.js';

export function resolveExampleVideoSource(
  value: string | undefined,
): string | undefined {
  const candidate = value?.trim();
  return candidate === '' ? undefined : candidate;
}

export const EXAMPLE_VIDEO_SRC = resolveExampleVideoSource(
  import.meta.env['VITE_HERO_VIDEO_URL'],
);
export const EXAMPLE_VIDEO_POSTER = '/early-access-example-poster.webp';

export interface ExampleVideo {
  readonly playing: boolean;
  readonly muted: boolean;
  /** Less motion was asked for: no autoplay. */
  readonly reduced: boolean;
  /** The portal that renders the element; mount it once, anywhere on the page. */
  readonly element: ReactNode;
  /** Callback ref for the inline frame's media host. */
  readonly inlineHost: (host: HTMLElement | null) => void;
  /** Callback ref for the enlarged view's frame; `null` returns the clip inline. */
  readonly enlargedHost: (host: HTMLElement | null) => void;
  readonly togglePlay: () => void;
  readonly toggleSound: () => void;
}

const noop = () => undefined;

export function useExampleVideo({ label }: { readonly label: string }): ExampleVideo {
  const reduced = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const video = useRef<HTMLVideoElement | null>(null);
  const inline = useRef<HTMLElement | null>(null);
  const [slot] = useState(() => {
    const element = document.createElement('div');
    element.className = 'cs-video-slot';
    return element;
  });

  const play = useCallback(() => {
    const element = video.current;
    if (element === null) return;
    void element.play().catch(noop);
  }, []);

  const inlineHost = useCallback(
    (host: HTMLElement | null) => {
      inline.current = host;
      if (host !== null && slot.parentElement === null) host.append(slot);
    },
    [slot],
  );

  const enlargedHost = useCallback(
    (host: HTMLElement | null) => {
      if (host !== null) {
        host.append(slot);
        return;
      }
      inline.current?.append(slot);
    },
    [slot],
  );

  const attach = useCallback(
    (element: HTMLVideoElement | null) => {
      video.current = element;
      if (element === null) return;
      // The `muted` content attribute, so the element is muted before any
      // script runs and stays muted across a reload of the same element.
      element.defaultMuted = true;
      element.muted = true;
      if (reduced) element.pause();
    },
    [reduced],
  );

  const togglePlay = useCallback(() => {
    const element = video.current;
    if (element === null) return;
    if (playing) element.pause();
    else play();
  }, [play, playing]);

  const toggleSound = useCallback(() => {
    const element = video.current;
    if (element === null) return;
    element.muted = !element.muted;
    setMuted(element.muted);
    if (!element.muted && !playing) play();
  }, [play, playing]);

  // Less motion requested after the clip started: stop it.
  useEffect(() => {
    if (reduced) video.current?.pause();
  }, [reduced]);

  const element = useMemo(
    () =>
      createPortal(
        createElement('video', {
          ref: attach,
          className: 'cs-video',
          src: EXAMPLE_VIDEO_SRC,
          poster: EXAMPLE_VIDEO_POSTER,
          muted: true,
          loop: true,
          playsInline: true,
          preload: 'metadata',
          autoPlay: !reduced,
          'aria-label': label,
          onPlay: () => {
            setPlaying(true);
          },
          onPause: () => {
            setPlaying(false);
          },
          onEnded: () => {
            setPlaying(false);
          },
          onVolumeChange: (event: { readonly currentTarget: HTMLVideoElement }) => {
            setMuted(event.currentTarget.muted);
          },
          onLoadedData: () => {
            if (!reduced) play();
          },
        }),
        slot,
      ),
    [attach, label, play, reduced, slot],
  );

  return {
    playing,
    muted,
    reduced,
    element,
    inlineHost,
    enlargedHost,
    togglePlay,
    toggleSound,
  };
}
